import { inject, Injectable, InjectionToken } from '@angular/core';
import { FaroInitConfig, withVersion } from 'tofe-core';

export interface AppConfig {
  apiBaseUrl: string;
  environment: string;
  googlePlacesApiKey?: string;
  eid: {
    eidBaseUrl: string;
    TOFEReturnUrl: string;
    infocertIdp: string;
    mysignUrl: string;
    idpRecoveryUrl: string;
  };
  faro?: FaroInitConfig;
  videoId?: {
    ipWhitelist?: boolean;
  };
}

export const APP_CONFIG = new InjectionToken<AppConfig>('APP_CONFIG');

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly config = inject(APP_CONFIG);

  get apiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }

  get environment(): string {
    return this.config.environment;
  }

  get googlePlacesApiKey(): string | undefined {
    return this.config.googlePlacesApiKey;
  }

  get eid() {
    return this.config.eid;
  }

  get faro(): FaroInitConfig | undefined {
    return this.config.faro;
  }

  /**
   * Whether the videoid meeting session must be opened with OpenTok's IP whitelist
   * feature (PLAIN-28444), so the media/signalling traffic stays inside the restricted
   * set of Vonage IPs that can be allowed through a corporate firewall.
   * Defaults to enabled: an environment that needs it off has to say so explicitly,
   * otherwise a config.json that predates the key would silently turn it off.
   */
  get videoIdIpWhitelist(): boolean {
    return this.config.videoId?.ipWhitelist ?? true;
  }
}

const DEFAULT_CONFIG: AppConfig = {
  apiBaseUrl: localStorage.getItem('apiBaseUrl') ?? window.location.origin + '/api',
  environment: 'local',
  googlePlacesApiKey: undefined,
  eid: {
    eidBaseUrl: 'https://top-stage.infocert.it/eid/start',
    TOFEReturnUrl: 'https://toponboarding-local.infocert.it',
    infocertIdp: 'https://identitycl.infocert.it',
    mysignUrl: 'https://mysigncl.infocert.it/',
    idpRecoveryUrl: 'https://identitycl.infocert.it/recovery',
  },
  videoId: {
    ipWhitelist: true,
  },
};

/**
 * config.json is stamped with the build id (`?v=<build id>`, see `withVersion`), so a new build is
 * not served from the browser cache. The build id must already be set (`setBuildId`) when this runs.
 * A change to config.json alone, deployed with the same build, is not seen by browsers that already
 * loaded that build: ship a new build to refresh them.
 */
export async function loadAppConfig(): Promise<AppConfig> {
  try {
    const response = await fetch(withVersion('/assets/config.json'));
    if (!response.ok) {
      console.warn('Failed to load config.json, using defaults');
      return DEFAULT_CONFIG;
    }
    const config = await response.json();
    return { ...DEFAULT_CONFIG, ...config };
  } catch (error) {
    console.warn('Failed to load config.json, using defaults', error);
    return DEFAULT_CONFIG;
  }
}
